// src/main.tsx
import "./index.css";
import { createRoot } from "react-dom/client";
import App from "./App";
import { setDeferredPrompt } from "./utils/pwaPromptBus";

// --- Экстренный сброс SW: ?swreset=1 ---
if (location.search.includes("swreset=1") && "serviceWorker" in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .then(() => caches.keys())
    .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
    .finally(() => {
      const clean = location.origin + location.pathname;
      location.replace(clean);
    });
}

// --- Диагностический оверлей (видно даже на телефоне без DevTools) ---
(function attachGlobalErrorOverlay() {
  const show = (msg: string) => {
    const div = document.createElement("div");
    div.style.cssText =
      "position:fixed;z-index:99999;left:0;top:0;right:0;padding:12px;" +
      "background:#fff3f3;color:#b00020;font:12px/1.4 system-ui, -apple-system, Segoe UI, Roboto, sans-serif;" +
      "border-bottom:1px solid #ffcdd2;white-space:pre-wrap";
    div.textContent = "JS Error: " + msg;
    document.body.prepend(div);
  };
  window.addEventListener("error", (e) => show(`${e.message}\n${e.filename}:${e.lineno}:${e.colno}`));
  window.addEventListener("unhandledrejection", (e: any) => show(`Unhandled: ${e?.reason?.message || String(e.reason || e)}`));
})();

// --- PWA install: шина deferredPrompt ---
window.addEventListener("beforeinstallprompt", (e: any) => {
  e.preventDefault();
  setDeferredPrompt(e);
});
window.addEventListener("appinstalled", () => {
  setDeferredPrompt(null);
});

// --- Гибридное обновление PWA (безопасно, с защитой) ---
(async () => {
  try {
    const { registerSW } = await import("virtual:pwa-register");
    const updateSW = registerSW({
      onNeedRefresh() {
        updateSW(true);
      },
      onOfflineReady() {
        // можно показать тост "Готово работать офлайн"
      },
    });
    // подстраховка: проверяем обновления раз в 30 минут
    setInterval(() => {
      try {
        updateSW();
      } catch {}
    }, 30 * 60 * 1000);
  } catch (e) {
    // если модуль не доступен — просто работаем без PWA-апдейтов
    console.warn("PWA register not available:", e);
  }
})();

// --- Постоянное хранилище (SW-STRATEGIES, решение владельца 18.09.2026) ---
// Просим браузер не вытеснять IndexedDB/Cache Storage под давлением места.
// Метод есть не везде (Safari/старые браузеры) — try/catch, отказ не роняет
// приложение, результат логируется одной строкой.
(async () => {
  try {
    if (navigator.storage?.persist) {
      const granted = await navigator.storage.persist();
      console.log("[storage] persist:", granted);
    }
  } catch (e) {
    console.error("[storage] persist request failed:", e instanceof Error ? e.message : String(e));
  }
})();

// --- Явная запись оболочки при старте (SW-PRELOAD, часть 3, 19.09.2026) ---
// Причина (разведка SW-FALLBACK-RECON): приложение маршрутизирует через
// BrowserRouter — переходы внутри не порождают навигационный fetch, воркеру
// нечего перехватывать. captureShellPlugin (sw.ts) поэтому обновляет
// /__app_shell__ только на холодном старте — оболочка существует только для
// start_url. Здесь — независимый путь того же результата: при каждом живом
// запуске сами кладём текущий документ под тот же ключ. Не заменяет
// captureShellPlugin (тот остаётся рабочим для случаев настоящей навигации,
// например обновления страницы) — этот путь добавлен рядом.
// Ключи ("app-shell" / "/__app_shell__") продублированы литералами — sw.ts
// исполняется в отдельном контексте (worker), общий модуль между ними не
// делится; при переименовании ключа в sw.ts поправить и здесь.
async function writeShellOnStart(): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  try {
    const res = await fetch(window.location.href, { credentials: "same-origin" });
    if (!res.ok) return;
    const shellCache = await caches.open("app-shell");
    await shellCache.put("/__app_shell__", res);
    console.log("[shell] captured on start:", window.location.pathname);
  } catch {
    // отказ — молча: сеть недоступна, или Cache Storage недоступен
    // (приватный режим/квота/старый браузер) — приложение это не чувствует.
  }
}

// --- Bootstrap приложения ---
async function bootstrap() {
  createRoot(document.getElementById("root")!).render(<App />);
  try {
    sessionStorage.removeItem("mira_reload_attempts");
  } catch {}
  void writeShellOnStart();
}

bootstrap();
